import type { ReactNode } from 'react';
import { Fragment } from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator } from '@savvy-kit/components/breadcrumb';
import IconButton from '@savvy-kit/components/icon-button';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { Menu } from 'lucide-react';

import { loggedInAsAdvisorID } from '@savvy/deprecated-shared/utils/savvy-auth';

import ClientHeaderCard from '../advisor/client-header-card';

type DashboardNavbarProps = {
  onOpenSidebar?: () => void;
  readonly useClientHeader?: boolean;
  readonly hideBorder?: boolean;
  readonly title?: string;
  readonly breadcrumbItems?: React.ReactElement[];
  readonly rightContent?: ReactNode;
} & React.ComponentProps<'header'>;

export function DashboardNavbar(props: DashboardNavbarProps) {
  const { onOpenSidebar, useClientHeader, title, breadcrumbItems, rightContent, hideBorder, className, ...other } =
    props;

  const isAdvisorLoggedInAsClient = !!loggedInAsAdvisorID();

  return (
    <header
      className={cn(
        // lg:left-64 / lg:w-[calc(100%-16rem)] track `sidebarWidth` (256px) in
        // deprecated-shared/theme — the permanent sidebar this bar sits beside.
        'fixed right-0 left-auto z-[1200] flex w-full shrink-0 flex-col bg-[var(--savvy-background-default)] text-[var(--savvy-neutral-900)] lg:left-64 lg:w-[calc(100%-16rem)]',
        hideBorder !== true && 'border-b border-solid border-b-[color:var(--savvy-divider)]',
        isAdvisorLoggedInAsClient ? 'top-16' : 'top-0',
        className,
      )}
      {...other}
    >
      <div className="relative flex min-h-16 items-center">
        <IconButton
          variant="ghost"
          aria-label="Open navigation menu"
          onClick={onOpenSidebar}
          className="ml-2 inline-flex lg:hidden"
        >
          <Menu />
        </IconButton>
        <div className="ml-10">
          {useClientHeader && (
            <div>
              <ClientHeaderCard breadcrumbItems={breadcrumbItems} />
            </div>
          )}
          {!useClientHeader && breadcrumbItems && breadcrumbItems.length > 1 && (
            <Breadcrumb aria-label="breadcrumb">
              <BreadcrumbList>
                {breadcrumbItems.map((item, index) => (
                  <Fragment key={item.key ?? index}>
                    {index > 0 && <BreadcrumbSeparator className="[&>svg]:size-5" />}
                    <BreadcrumbItem>{item}</BreadcrumbItem>
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}
          {!useClientHeader && !breadcrumbItems && title && (
            <Text variant="subtitle-lg" color="dark">
              {title}
            </Text>
          )}
        </div>

        <div className="grow" />
        {rightContent && <div className="mr-8">{rightContent}</div>}
      </div>
    </header>
  );
}
